import React, { useState } from "react";
import { toast } from "react-toastify";
import Typeahead from "../Typeahead";
import ApiHandler from "../../ApiHandler";
import GlobalVariable from "../../GlobalVariable";

/**
 * Search by GENERIC name (content), on the Order screen - same flow as the
 * working mobile app:
 *
 *   type >= 2 chars (300 ms debounce)
 *   1. POST api/Search/GenericByNameGroup
 *        { wclientid, search: "<typed text>" }
 *      -> [{ genCode, genName }]                      shown as a pick-list
 *   pick a generic
 *   2. POST api/Search/ProductByGenericCodeGroupNew
 *        { wclientid, search: "<genCode>", areaCode }
 *      -> products with that content                  shown as a table
 *   (reload) on a product - here OR in the product-name search dropdown
 *   2 again with search: "<prodCode before '.'>.<genCode>"  e.g. 5811997.37
 *      -> every product with the same content as that one
 *   pick a product -> onPick(product): the same quantity dialog as the
 *   product search.
 *
 * wclientid is the GROUP id (GlobalVariable.groupId): the "...Group"
 * endpoints search across every firm in the group.
 *
 * Exports used by the Order screen's product-name search as well:
 *   useContentSearch()  the step-2 call + what it is showing
 *   ContentResults      the products table (with reload buttons)
 *   ReloadButton        the reload button itself
 *   hasGenCode(p)       whether a product can be reloaded
 */

const val = (row, keys) => {
    for (let i = 0; i < keys.length; i++) {
        const v = row && row[keys[i]];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
};

// GenNameModel(genCode, genName); a couple of spellings tolerated
const toGeneric = (row) => ({
    ...row,
    genCode: val(row, ["genCode", "gencode", "GenCode"]),
    genName: String(val(row, ["genName", "genname", "GenName"])),
});

// the key for the reload: "<prodCode before '.'>.<genCode>"
const sameContentKey = (p) =>
    String(p.prodCode === undefined || p.prodCode === null ? "" : p.prodCode).split(".")[0] +
    "." +
    p.genCode;

export const hasGenCode = (p) => !!p && p.genCode !== undefined && p.genCode !== null &&
    String(p.genCode).trim() !== "" && Number(p.genCode) !== 0;

const GENERIC_COLUMNS = [{ header: "Generic Name", field: "genName", cols: 12 }];

const user = () => {
    try {
        return JSON.parse(localStorage.getItem("user-info")) || {};
    } catch (e) {
        return {};
    }
};

/** Step 2 (GENERIC): the call, and the table state it fills. */
export function useContentSearch() {
    // what the table is showing: { title, search } or null
    const [shown, setShown] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadMeds = (search, title) => {
        setShown({ search, title });
        setProducts([]);
        setLoading(true);
        ApiHandler.post({
            url: "/api/Search/ProductByGenericCodeGroupNew/",
            data: {
                wclientid: GlobalVariable.groupId + "",
                search: search + "",
                areaCode: user().areaCode + "",
            },
        })
            .then((res) => {
                const rows = Array.isArray(res.data) ? res.data : [];
                // same mapping as the product search: the quantity as-is
                setProducts(rows.map((p) => ({ ...p, stock: p.stock })));
            })
            .catch((err) => {
                console.error("ProductByGenericCodeGroupNew", err);
                toast.error("Could not load the products");
            })
            .finally(() => setLoading(false));
    };

    // reload: everything with the same content as this product
    const reload = (p) =>
        loadMeds(
            sameContentKey(p),
            <>Same content as <b>{p.prodName || "this product"}</b></>
        );

    const clear = () => {
        setShown(null);
        setProducts([]);
    };

    return { shown, products, loading, loadMeds, reload, clear };
}

/** The reload button. Does not let the click reach the row / menu item. */
export const ReloadButton = ({ product, onReload }) => (
    <button
        type="button"
        className="genReload"
        title="Show all products with the same content"
        aria-label={"Show all products with the same content as " + (product.prodName || "this product")}
        onMouseDown={(e) => {
            // keep focus where it is (in the search box)
            e.preventDefault();
            e.stopPropagation();
        }}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onReload(product);
        }}
    >
        &#x27F3;
    </button>
);

/** The products table for a content search (or a reload). */
export function ContentResults({ content, columns, onPick, onClose }) {
    const { shown, products, loading, reload } = content;
    if (!shown) return null;
    const cols = columns || [];

    return (
        <div className="genResults">
            <div className="genResultsHead">
                <span>{shown.title}</span>
                {!loading && <span className="genCount">{products.length}</span>}
                {onClose ? (
                    <button
                        type="button"
                        className="genClose"
                        aria-label="Close these results"
                        title="Close"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                ) : null}
            </div>

            {loading ? (
                <p className="genEmpty">Loading products...</p>
            ) : products.length === 0 ? (
                <p className="genEmpty">No products found.</p>
            ) : (
                <div className="genTableScroll">
                    <table className="genTable">
                        <thead>
                            <tr>
                                {cols.map((c) => (
                                    <th key={c.field}>{c.header}</th>
                                ))}
                                <th className="genReloadHead" title="Same content">
                                    Same content
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p, i) => (
                                <tr
                                    key={(p.prodCode || "") + "_" + i}
                                    tabIndex={0}
                                    onClick={() => onPick(p)}
                                    onKeyDown={(e) => {
                                        if (e.target !== e.currentTarget) return;
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            onPick(p);
                                        }
                                    }}
                                    title="Click to set the quantity"
                                >
                                    {cols.map((c) => (
                                        <td key={c.field} data-label={c.header}>
                                            {p[c.field] === undefined || p[c.field] === null
                                                ? ""
                                                : String(p[c.field])}
                                        </td>
                                    ))}
                                    <td className="genReloadCell" data-label="Same content">
                                        {hasGenCode(p) ? (
                                            <ReloadButton product={p} onReload={reload} />
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="genHint">
                Click a product to set its quantity. &#x27F3; shows every product with the same content.
            </p>
        </div>
    );
}

export default function GenericSearch({ columns, onPick, onFocus, onBlur }) {
    const [generics, setGenerics] = useState([]);
    const [selected, setSelected] = useState([]);
    const content = useContentSearch();

    // Step 1 - GENNAME: generics matching the typed text
    const searchGenerics = (text) => {
        if (!text || text.trim().length < 2) return;
        ApiHandler.post({
            url: "/api/Search/GenericByNameGroup/",
            data: {
                wclientid: GlobalVariable.groupId + "",
                search: text,
            },
        })
            .then((res) => {
                const rows = Array.isArray(res.data) ? res.data : [];
                setGenerics(rows.map(toGeneric).filter((g) => g.genName));
            })
            .catch((err) => {
                console.error("GenericByNameGroup", err);
                setGenerics([]);
            });
    };

    const pickGeneric = (g) => {
        setSelected(g ? [g] : []);
        if (!g) {
            content.clear();
            return;
        }
        if (g.genCode === "") {
            toast.error("This generic has no code - cannot load its products");
            return;
        }
        content.loadMeds(g.genCode, <>Products for <b>{g.genName}</b></>);
    };

    return (
        <div className="genSearch">
            <Typeahead
                // no inline zIndex: it overrode the CSS that keeps this box
                // (and its dropdown) above the products panel below it
                style={{ width: "100%" }}
                placeholder="Search Generic / Content (e.g. Paracetamol)"
                id="searchGeneric"
                className="searchProduct"
                labelKey="genName"
                name="genName"
                columns={GENERIC_COLUMNS}
                colWidth={window.screen.width}
                isLoading={false}
                delay={300}
                onFocus={onFocus}
                onBlur={onBlur}
                onSearch={searchGenerics}
                onChange={(e) => pickGeneric(e[0] || null)}
                options={generics}
                selected={selected}
            />

            <ContentResults content={content} columns={columns} onPick={onPick} />
        </div>
    );
}
